import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWorkflowPanelStore } from '@/stores/workflow-panel-store';
import { useNodeStore, selectSelectedNodeIds } from '@/stores/node-store';
import { useShallow } from 'zustand/react/shallow';
import { useEventsSummary } from '@/hooks/use-events-summary';
import { EventsSummary } from './events-summary';
import { EventsNodeDetail } from './events-node-detail';
import type { NodeStatusEvent } from '@/types/workflow-types';

interface EventsTabContentProps {
  nodeStatuses: Record<string, NodeStatusEvent[]>;
  nodeId?: string | null;
  onFocusNode?: (nodeId: string) => void;
  isExecuting?: boolean;
  isPaused?: boolean;
  isConnected?: boolean;
}

export function EventsTabContent({
  nodeStatuses,
  nodeId = null,
  onFocusNode,
  isExecuting = false,
  isPaused = false,
  isConnected = false,
}: EventsTabContentProps) {
  const { eventsDetailNodeId, setEventsDetailNodeId } = useWorkflowPanelStore();
  const selectedNodeIds = useNodeStore(useShallow(selectSelectedNodeIds));
  const nodes = useNodeStore((s) => s.nodes);

  // Freeze the summary counts when paused so trailing backend events don't
  // keep incrementing the total after the "Workflow paused" toast appears.
  // Per-node detail (EventsNodeDetail) still gets live data.
  const [frozenNodeStatuses, setFrozenNodeStatuses] = useState<Record<
    string,
    NodeStatusEvent[]
  > | null>(null);

  useEffect(() => {
    if (isPaused) {
      setFrozenNodeStatuses((prev) => {
        // Keep the snapshot once it has data. Skip empty objects — isPaused
        // can arrive before nodeStatuses hydrates on reconnect, and we don't
        // want to freeze an empty map.
        if (prev !== null && Object.keys(prev).length > 0) return prev;
        return Object.keys(nodeStatuses).length > 0 ? nodeStatuses : null;
      });
    } else {
      setFrozenNodeStatuses(null);
    }
  }, [isPaused, nodeStatuses]);

  // Use live data when not paused to avoid a stale flash right after resume.
  const summaryStatuses = !isPaused
    ? nodeStatuses
    : (frozenNodeStatuses ?? nodeStatuses);
  const summary = useEventsSummary(summaryStatuses, nodes, isPaused);

  // A node's card speaks for its own node; the workflow card follows whichever
  // node the summary drilled into.
  const activeNodeId =
    nodeId ?? selectedNodeIds[0] ?? eventsDetailNodeId ?? null;

  const activeNodeStats = useMemo(
    () =>
      activeNodeId
        ? summary.nodes.find((n) => n.nodeId === activeNodeId)
        : undefined,
    [activeNodeId, summary.nodes],
  );

  const handleSelectNode = useCallback(
    (nodeId: string) => {
      setEventsDetailNodeId(nodeId);
      onFocusNode?.(nodeId);
    },
    [setEventsDetailNodeId, onFocusNode],
  );

  const handleBack = useCallback(() => {
    setEventsDetailNodeId(null);
    const { nodes } = useNodeStore.getState();
    const updatedNodes = nodes.map((n) => ({ ...n, selected: false }));
    useNodeStore.setState({ nodes: updatedNodes, lastSelectedNodeId: null });
  }, [setEventsDetailNodeId]);

  if (activeNodeId) {
    return (
      <EventsNodeDetail
        nodeId={activeNodeId}
        nodeStats={activeNodeStats}
        modelFailures={summary.modelFailures}
        retries={summary.retries}
        nodeStatuses={nodeStatuses}
        onBack={nodeId ? undefined : handleBack}
        isConnected={isConnected}
        isPaused={isPaused}
      />
    );
  }

  return (
    <EventsSummary
      summary={summary}
      onSelectNode={handleSelectNode}
      isExecuting={isExecuting}
    />
  );
}
